import type {ThreadUserMessagePart} from '@assistant-ui/core';
import {useAui} from '@assistant-ui/react';
import {useLayoutEffect, useRef} from 'react';

import type {
  SendMessageOptions,
  WBAgentController,
} from '../../agentChat/controller';
import {wbAgentControllerStore} from '../../agentChat/controller';
import {WANDB_DATA_PART_NAME} from '../../agentChat/messageConversions';
import type {ContentPart, WandbRunPart} from '../../agentChat/store/thread';
import {useTrackWBAgentComposer} from './useTrackWBAgent';
import {useWBAgentContextStore} from './WBAgentContextStore';

type Aui = ReturnType<typeof useAui>;
type TrackSent = ReturnType<typeof useTrackWBAgentComposer>['trackMessageSent'];

/**
 * WBAgentControllerBridge registers the imperative controller handle while the
 * agent chat is mounted, so buttons anywhere in the app can open the chat or
 * send a message. It must render inside `AuiProvider` (it reads `useAui`) and
 * is mounted only when the feature is available; the handle is cleared on
 * unmount. Renders nothing.
 */
export const WBAgentControllerBridge = (): null => {
  const aui = useAui();
  const {trackMessageSent} = useTrackWBAgentComposer();

  // Hold the tracker in a ref so the handle's identity doesn't churn on route
  // changes (which would re-register on every navigation).
  const trackRef = useRef(trackMessageSent);
  trackRef.current = trackMessageSent;

  // NOTE: Layout effect clears the handle before paint, beating any click that
  // would otherwise reach a handle whose runtime is being torn down.
  useLayoutEffect(
    () =>
      wbAgentControllerStore.registerHandle(
        createController(aui, () => trackRef.current)
      ),
    [aui]
  );

  return null;
};

function createController(
  aui: Aui,
  getTrackSent: () => TrackSent
): WBAgentController {
  const openChat = () => {
    useWBAgentContextStore.getState().setOpen(true);
  };

  return {
    openChat,
    sendMessage(
      prompt: string,
      attachments?: ContentPart[],
      opts?: SendMessageOptions
    ) {
      const {newChat = true} = opts ?? {};
      const runParts = (attachments ?? []).filter(isWandbRunPart);
      const content = buildUserContent(prompt, runParts);

      // Nothing to send is a no-op; don't open or switch threads.
      if (content.length === 0) {
        return;
      }

      if (newChat) {
        aui.threads().switchToNewThread();
      }

      aui.thread().append({role: 'user', content});
      getTrackSent()('programmatic');
      openChat();
    },
  };
}

function buildUserContent(
  prompt: string,
  runParts: WandbRunPart[]
): ThreadUserMessagePart[] {
  const content: ThreadUserMessagePart[] = runParts.map(run => ({
    type: 'data',
    name: WANDB_DATA_PART_NAME,
    data: run,
  }));

  const text = prompt.trim();
  if (text.length > 0) {
    content.push({type: 'text', text});
  }

  return content;
}

function isWandbRunPart(part: ContentPart): part is WandbRunPart {
  return part.type === 'wandb-run';
}
